import type { AgentChatMetadata } from '../../views/ai-agent/ai-agent.types';

/**
 * Normalizes tenant ATS / portal host for building record URLs (aligns with Domain `normalizeAtsPortalBaseUrl`).
 */
export function normalizeAtsPortalOrigin(raw: string | null | undefined): string | null {
  if (raw == null) {
    return null;
  }
  const s = String(raw).trim();
  if (!s) {
    return null;
  }
  try {
    let candidate = s;
    if (!/^https?:\/\//i.test(candidate)) {
      candidate = `https://${candidate}`;
    }
    const u = new URL(candidate);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') {
      return null;
    }
    return u.origin.replace(/\/$/, '');
  } catch {
    return null;
  }
}

/** Relative path (no trailing slash) before the encrypted id segment. */
export function hyperlinkPathForModule(moduleRaw: string): string | null {
  const m = moduleRaw.trim().toLowerCase();
  if (m === 'applicants' || m === 'applicant' || m === 'candidate' || m === 'candidates') {
    return '/ApplicantProfiles/snapshot';
  }
  if (
    m === 'bench' ||
    m === 'bench_sales' ||
    m === 'benchsales' ||
    m === 'talent_bench' ||
    m === 'talentbench'
  ) {
    return '/BenchSales/snapshot';
  }
  if (
    m === 'job_postings' ||
    m === 'job_posting' ||
    m === 'jobs' ||
    m === 'job' ||
    m === 'requisition' ||
    m === 'requisitions' ||
    m === 'opening' ||
    m === 'openings'
  ) {
    return '/job_postings/job_snapshot';
  }
  if (m === 'submissions' || m === 'submission') {
    return '/job_postings/applied_ext_candidate';
  }
  if (m === 'clients' || m === 'client' || m === 'config_clients') {
    return '/clients/snapshot';
  }
  if (m === 'placements' || m === 'placement') {
    return '/Placements/snapshot';
  }
  return null;
}

function trimmedIdString(v: unknown): string | null {
  if (typeof v === 'string' && v.trim()) {
    return v.trim();
  }
  if (typeof v === 'number' && Number.isFinite(v)) {
    return String(v);
  }
  return null;
}

/** Row keys produced by Domain `applyEncryptedIdToBqRows` (see `BQ_MODULE_ENCRYPTED_FIELDS`) + legacy. */
const BQ_TECH_ENCRYPTED_ID_KEYS_LOWER = new Set([
  'encryptedid',
  'encrypted_id',
  'jobencryptedid',
  'job_encrypted_id',
  'applicantencryptedid',
  'applicant_encrypted_id',
  'benchencryptedid',
  'bench_encrypted_id',
  'clientencryptedid',
  'client_encrypted_id',
  'submissionencryptedid',
  'submission_encrypted_id',
  'placementencryptedid',
  'placement_encrypted_id',
  'placementemployeeencryptedid',
  'placement_employee_encrypted_id',
]);

/** True when this row key holds the ATS encrypted id used for hyperlinks — hide from table/compact UI. */
export function isBqEncryptedIdColumnKey(columnKey: string): boolean {
  return BQ_TECH_ENCRYPTED_ID_KEYS_LOWER.has(columnKey.trim().toLowerCase());
}

/** Legacy + module-specific keys, in try order (aligns with domain orch `bq-row-encrypted-id.util.ts`). */
const LEGACY_ENCRYPTED_ROW_KEYS = ['encryptedID', 'encrypted_id', 'encryptedId'] as const;

/**
 * Ordered row keys to read for the hyperlink `module` (Domain `hyperlink_columns[].module`),
 * then legacy `_row_pk_id` → `encryptedID` fields.
 */
function encryptedIdRowKeysForModule(moduleRaw: string): readonly string[] {
  const m = moduleRaw.trim().toLowerCase();
  const primary: string[] = [];

  if (m === 'applicants' || m === 'applicant' || m === 'candidate' || m === 'candidates') {
    primary.push('applicantEncryptedID', 'applicant_encrypted_id', '_enc_pk_applicants');
  } else if (
    m === 'bench' ||
    m === 'bench_sales' ||
    m === 'benchsales' ||
    m === 'talent_bench' ||
    m === 'talentbench'
  ) {
    primary.push('benchEncryptedID', 'bench_encrypted_id', '_enc_pk_bench');
  } else if (
    m === 'job_postings' ||
    m === 'job_posting' ||
    m === 'jobs' ||
    m === 'job' ||
    m === 'requisition' ||
    m === 'requisitions' ||
    m === 'opening' ||
    m === 'openings'
  ) {
    primary.push('jobEncryptedID', 'job_encrypted_id');
  } else if (m === 'submissions' || m === 'submission') {
    primary.push('submissionEncryptedID', 'submission_encrypted_id');
  } else if (m === 'clients' || m === 'client' || m === 'config_clients') {
    primary.push('clientEncryptedID', 'client_encrypted_id');
  } else if (m === 'placements' || m === 'placement') {
    primary.push(
      'placementEncryptedID',
      'placement_encrypted_id',
      '_enc_pk_placements',
    );
  }

  return [...primary, ...LEGACY_ENCRYPTED_ROW_KEYS];
}

/** Employee encrypted id for placement deep links (first URL segment). */
function placementEmployeeEncryptedIdFromRow(row: Record<string, unknown>): string | null {
  return firstTrimmedIdFromKeys(row, [
    'placementEmployeeEncryptedID',
    'placement_employee_encrypted_id',
    '_enc_pk_placement_employee',
    ...LEGACY_ENCRYPTED_ROW_KEYS,
  ]);
}

function isPlacementsHyperlinkModule(moduleRaw: string): boolean {
  const m = moduleRaw.trim().toLowerCase();
  return m === 'placements' || m === 'placement';
}

/**
 * Placement display columns that should open the ATS placement snapshot when encrypted ids exist:
 * placement name, placement email, placement code (and placement_id / placement code aliases).
 */
export function isPlacementHyperlinkDisplayColumn(columnKey: string): boolean {
  const parts = normalizedColumnParts(columnKey);
  if (parts.length === 0) {
    return false;
  }
  const joined = parts.join('_');
  if (
    joined === 'placement_name' ||
    joined === 'placement_email' ||
    joined === 'placement_code' ||
    joined === 'placement_id'
  ) {
    return true;
  }
  if (parts[0] === 'placement' || parts.includes('placement')) {
    const last = parts[parts.length - 1];
    return last === 'name' || last === 'email' || last === 'code' || last === 'id';
  }
  return false;
}

function firstTrimmedIdFromKeys(row: Record<string, unknown>, keys: readonly string[]): string | null {
  for (const key of keys) {
    const v = trimmedIdString(row[key]);
    if (v) {
      return v;
    }
  }
  return null;
}

/**
 * Encrypted id for ATS deep-link tail — from row fields for this `hyperlink_columns[].module`
 * (not the hyperlink display column).
 */
export function rowEncryptedIdForHyperlink(
  row: Record<string, unknown>,
  module: string,
): string | null {
  return firstTrimmedIdFromKeys(row, encryptedIdRowKeysForModule(module));
}

export function buildHyperlinkHref(
  portalRaw: string | null | undefined,
  module: string,
  encryptedId: string,
  encryptedIdSecondary?: string | null,
): string | null {
  const path = hyperlinkPathForModule(module);
  if (!path) {
    return null;
  }
  const id = encryptedId.trim();
  if (!id) {
    return null;
  }
  const origin = normalizeAtsPortalOrigin(portalRaw);
  if (!origin) {
    return null;
  }
  let href: string;
  if (isPlacementsHyperlinkModule(module)) {
    const employeeEnc = id;
    const placementEnc = (encryptedIdSecondary ?? '').trim();
    if (!placementEnc) {
      return null;
    }
    href = `${origin}${path}/${employeeEnc}/${placementEnc}`;
  } else {
    href = `${origin}${path}/${id}`;
  }
  try {
    const u = new URL(href);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') {
      return null;
    }
    return u.href;
  } catch {
    return null;
  }
}

/** Build ATS placement URL when both encrypted PKs are present on the row. */
export function buildPlacementHyperlinkHref(
  portalRaw: string | null | undefined,
  row: Record<string, unknown>,
): string | null {
  const employeeEnc = placementEmployeeEncryptedIdFromRow(row);
  const placementEnc = rowEncryptedIdForHyperlink(row, 'placements');
  if (!employeeEnc || !placementEnc) {
    return null;
  }
  return buildHyperlinkHref(portalRaw, 'placements', employeeEnc, placementEnc);
}

function isApplicantsHyperlinkModule(moduleRaw: string): boolean {
  const m = moduleRaw.trim().toLowerCase();
  return m === 'applicants' || m === 'applicant' || m === 'candidate' || m === 'candidates';
}

function isBenchHyperlinkModule(moduleRaw: string): boolean {
  const m = moduleRaw.trim().toLowerCase();
  return (
    m === 'bench' ||
    m === 'bench_sales' ||
    m === 'benchsales' ||
    m === 'talent_bench' ||
    m === 'talentbench'
  );
}

/** Applicant or Talent Bench seeker modules (binocular / identity columns). */
function isSeekerHyperlinkModule(moduleRaw: string): boolean {
  return isApplicantsHyperlinkModule(moduleRaw) || isBenchHyperlinkModule(moduleRaw);
}

function normalizedColumnParts(columnKey: string): string[] {
  return columnKey.trim().toLowerCase().split(/[_\s]+/).filter(Boolean);
}

/** True when the column key refers to applicant / candidate / job seeker / bench entities. */
function hasApplicantEntityInColumnParts(parts: string[]): boolean {
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i];
    if (p === 'applicant' || p === 'applicants' || p === 'candidate' || p === 'candidates') {
      return true;
    }
    /** Talent Bench public code (`bench_id`) — same seeker row, distinct display id. */
    if (p === 'bench') {
      return true;
    }
    if (p === 'jobseeker' || p === 'jobseekers') {
      return true;
    }
    if (p === 'job' && parts[i + 1] === 'seeker') {
      return true;
    }
    if (p === 'seeker' && parts[i - 1] === 'job') {
      return true;
    }
  }
  return false;
}

/**
 * True when the BQ column represents applicant / candidate / job seeker / bench identity or contact fields
 * (e.g. `applicant_id`, `bench_id`, `applicant_name`, `candidate name`, `candidate_email`, `job_seeker_id`).
 */
export function isApplicantRelatedDisplayColumn(columnKey: string): boolean {
  const parts = normalizedColumnParts(columnKey);
  return parts.length > 0 && hasApplicantEntityInColumnParts(parts);
}

/**
 * Binocular quick-view priority tier for applicant/candidate columns (lower = higher priority).
 * 1 — id, 2 — name, 3 — email; `null` — not eligible for binocular.
 */
export function applicantBinocularColumnTier(columnKey: string): 1 | 2 | 3 | null {
  const parts = normalizedColumnParts(columnKey);
  if (parts.length === 0 || !hasApplicantEntityInColumnParts(parts)) {
    return null;
  }
  const last = parts[parts.length - 1];
  if (last === 'id') {
    return 1;
  }
  if (last === 'name' || parts.includes('name')) {
    return 2;
  }
  if (last === 'email' || parts.includes('email')) {
    return 3;
  }
  return null;
}

/** Single column per row that should show the binocular icon (id → name → email). */
export function resolveApplicantBinocularColumnForRow(columnKeys: readonly string[]): string | null {
  let bestColumn: string | null = null;
  let bestTier = 4;

  for (const col of columnKeys) {
    const tier = applicantBinocularColumnTier(col);
    if (tier == null || tier >= bestTier) {
      continue;
    }
    bestTier = tier;
    bestColumn = col;
  }

  return bestColumn;
}

function hyperlinkModuleForApplicantDrawerColumn(
  meta: AgentChatMetadata | null | undefined,
  columnKey: string,
): string | null {
  const specs = meta?.hyperlink_columns;
  if (!Array.isArray(specs) || specs.length === 0) {
    return null;
  }
  const colKey = columnKey.trim().toLowerCase();
  const spec = specs.find((h) => h.column.trim().toLowerCase() === colKey);
  if (!spec || !isSeekerHyperlinkModule(spec.module)) {
    return null;
  }
  return spec.module;
}

/**
 * Encrypted applicant id for the quick-view (binocular) drawer.
 * Only the highest-priority column per row (id → name → email) receives the icon.
 */
export function applicantEncryptedIdForBqDrawer(
  row: Record<string, unknown>,
  columnKey: string,
  meta?: AgentChatMetadata | null,
  columnKeys?: readonly string[],
): string | null {
  if (applicantBinocularColumnTier(columnKey) == null) {
    return null;
  }

  const keys = columnKeys ?? [columnKey];
  if (resolveApplicantBinocularColumnForRow(keys) !== columnKey) {
    return null;
  }

  const hyperlinkModule = hyperlinkModuleForApplicantDrawerColumn(meta, columnKey);
  const id = rowEncryptedIdForHyperlink(
    row,
    hyperlinkModule ?? (normalizedColumnParts(columnKey).includes('bench') ? 'bench' : 'applicants'),
  );
  return id?.trim() ? id.trim() : null;
}

/** Full `https` URL for a BQ cell when `hyperlink_columns` matches and row has encrypted id + portal base. */
export function bqHyperlinkHrefForCell(
  meta: AgentChatMetadata | null,
  portalBase: string | null | undefined,
  row: Record<string, unknown>,
  column: string,
): string | null {
  if (!meta || meta.type !== 'bq') {
    return null;
  }
  const specs = meta.hyperlink_columns;
  const colKey = column.trim().toLowerCase();

  if (Array.isArray(specs) && specs.length > 0) {
    const spec = specs.find((h) => h.column.trim().toLowerCase() === colKey);
    if (spec) {
      if (isPlacementsHyperlinkModule(spec.module)) {
        return buildPlacementHyperlinkHref(portalBase ?? null, row);
      }
      const encId = rowEncryptedIdForHyperlink(row, spec.module);
      if (!encId) {
        return null;
      }
      return buildHyperlinkHref(portalBase ?? null, spec.module, encId);
    }
  }

  /**
   * Placement name / email / code: link whenever both encrypted PKs exist on the row
   * (even if Domain only listed one placement column in hyperlink_columns).
   */
  if (isPlacementHyperlinkDisplayColumn(column)) {
    return buildPlacementHyperlinkHref(portalBase ?? null, row);
  }

  return null;
}
